import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer, Chip, TextField, MenuItem, Alert, CircularProgress,
  Badge, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack,
} from '@mui/material';
import { tasksAPI, projectsAPI, manufacturingAPI, techniciansAPI } from '../services/api';
import { STATUS_LABELS, STATUS_COLORS } from './technician/taskConstants';
import TaskTemplates from '../components/common/TaskTemplates';
import PendingPhotos from '../components/common/PendingPhotos';
import TaskEditorDialog from '../components/common/TaskEditorDialog';
import TaskHtmlDocs from '../components/common/TaskHtmlDocs';

const fmtHours = (v) => (Number(v) || 0).toFixed(1);

/**
 * Vista de tareas para el gestor: listado global con filtros, bandeja de tareas
 * sin orden de fabricacion e informe de previsto frente a real.
 */
export default function Tasks() {
  const [tab, setTab] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [unassigned, setUnassigned] = useState({ count: 0, tasks: [] });
  const [report, setReport] = useState(null);
  const [projects, setProjects] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState(0);

  const [filters, setFilters] = useState({ project_id: '', technician_id: '', status: '', overdue: '' });

  // Editor de la tarea: datos, checklist y comentarios
  const [editor, setEditor] = useState(null);            // { taskId } | null

  // Dialogo para asignar OF a una tarea que nacio sin ella
  const [orderDialog, setOrderDialog] = useState(null);  // task
  const [orders, setOrders] = useState([]);
  const [chosenOrder, setChosenOrder] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.technician_id) params.technician_id = filters.technician_id;
      if (filters.status) params.status = filters.status;
      if (filters.overdue) params.overdue = true;

      const [listRes, unassignedRes, reportRes, photosRes] = await Promise.all([
        tasksAPI.list(params),
        tasksAPI.unassignedOrder(),
        tasksAPI.plannedVsActual(filters.project_id ? { project_id: filters.project_id } : {}),
        // Solo para el contador de la pestana: la bandeja se carga sola al abrirla
        tasksAPI.pendingPhotos().catch(() => ({ data: { count: 0 } })),
      ]);
      setTasks(listRes.data);
      setUnassigned(unassignedRes.data);
      setReport(reportRes.data);
      setPendingPhotos(photosRes.data.count || 0);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido cargar las tareas.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    projectsAPI.list().then((r) => setProjects(r.data)).catch(() => {});
    techniciansAPI.list().then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  const openOrderDialog = async (task) => {
    setOrderDialog(task);
    setChosenOrder('');
    if (task.project_id) {
      try {
        const res = await manufacturingAPI.list(task.project_id);
        setOrders(res.data);
      } catch (_) { setOrders([]); }
    } else {
      setOrders([]);
    }
  };

  const assignOrder = async () => {
    try {
      await tasksAPI.setManufacturingOrder(orderDialog.id, chosenOrder || null);
      setOrderDialog(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido asignar la orden.');
    }
  };

  const taskTable = (rows, showOrderAction) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#c62828' }}>
            {['Codigo', 'Tarea', 'Proyecto', 'OF', 'Asignada a', 'Fechas', 'Prev.', 'Real', 'Estado']
              .map((h) => (
                <TableCell key={h} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>
              ))}
            {showOrderAction && <TableCell sx={{ color: '#fff' }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.id} hover onClick={() => setEditor({ taskId: t.id })}
              sx={{ cursor: 'pointer', ...(t.is_overdue ? { backgroundColor: '#ffebee' } : {}) }}>
              <TableCell>{t.code}</TableCell>
              <TableCell>{t.title}</TableCell>
              <TableCell>{t.project_name || '—'}</TableCell>
              <TableCell>{t.manufacturing_order_code || '—'}</TableCell>
              <TableCell>
                {t.assignee?.name || <Typography variant="body2" color="error">Sin asignar</Typography>}
                {t.created_in_field && <Chip size="small" label="campo" variant="outlined" sx={{ ml: 0.5 }} />}
              </TableCell>
              <TableCell>{t.planned_start || '—'}{t.planned_end ? ` / ${t.planned_end}` : ''}</TableCell>
              <TableCell align="right">{t.estimated_hours ?? '—'}</TableCell>
              <TableCell align="right">{t.actual_hours || 0}</TableCell>
              <TableCell>
                <Chip size="small" label={STATUS_LABELS[t.status] || t.status}
                  color={STATUS_COLORS[t.status] || 'default'} />
              </TableCell>
              {showOrderAction && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button size="small" onClick={() => openOrderDialog(t)}>Asignar OF</Button>
                </TableCell>
              )}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  No hay tareas que mostrar.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>Tareas</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`Todas (${tasks.length})`} />
          <Tab label={
            <Badge badgeContent={unassigned.count} color="error" sx={{ pr: 1.5 }}>
              Sin orden
            </Badge>
          } />
          <Tab label={
            <Badge badgeContent={pendingPhotos} color="warning" sx={{ pr: 1.5 }}>
              Fotos a Drive
            </Badge>
          } />
          <Tab label="Previsto vs real" />
          <Tab label="Plantillas" />
          <Tab label="HTML" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ p: 2, flexWrap: 'wrap' }}>
            <TextField select size="small" label="Proyecto" sx={{ minWidth: 200 }}
              value={filters.project_id}
              onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.code || p.offer_code} — {p.name}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Tecnico" sx={{ minWidth: 180 }}
              value={filters.technician_id}
              onChange={(e) => setFilters({ ...filters, technician_id: e.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              {technicians.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Estado" sx={{ minWidth: 160 }}
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Retraso" sx={{ minWidth: 140 }}
              value={filters.overdue}
              onChange={(e) => setFilters({ ...filters, overdue: e.target.value })}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="1">Solo retrasadas</MenuItem>
            </TextField>
          </Stack>
        </Paper>
      )}

      {tab === 2 ? (
        <Paper variant="outlined">
          <PendingPhotos onCountChange={setPendingPhotos} />
        </Paper>
      ) : tab === 4 ? (
        <Paper variant="outlined"><TaskTemplates /></Paper>
      ) : tab === 5 ? (
        <Paper variant="outlined"><TaskHtmlDocs /></Paper>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined">
          {tab === 0 && taskTable(tasks, false)}
          {tab === 1 && (
            <>
              <Alert severity="info" sx={{ m: 2 }}>
                Tareas creadas sin orden de fabricacion. Puedes asignarles una orden existente,
                crear una nueva, o dejarlas asi si no procede.
              </Alert>
              {taskTable(unassigned.tasks, true)}
            </>
          )}
          {tab === 3 && report && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#c62828' }}>
                    {['Orden', 'Tareas', 'Terminadas', 'Previsto (h)', 'Real (h)', 'Desviacion']
                      .map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((r) => (
                    <TableRow key={r.manufacturing_order_id ?? 'none'} hover>
                      <TableCell>
                        {r.manufacturing_order_code || '—'} {r.manufacturing_order_name}
                      </TableCell>
                      <TableCell>{r.tasks}</TableCell>
                      <TableCell>{r.done}</TableCell>
                      <TableCell align="right">{fmtHours(r.estimated_hours)}</TableCell>
                      <TableCell align="right">{fmtHours(r.actual_hours)}</TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={`${r.deviation_hours > 0 ? '+' : ''}${fmtHours(r.deviation_hours)} h`}
                          color={r.deviation_hours > 0 ? 'warning' : 'success'} />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{report.totals.tasks}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{report.totals.done}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {fmtHours(report.totals.estimated_hours)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {fmtHours(report.totals.actual_hours)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {report.totals.deviation_hours > 0 ? '+' : ''}
                      {fmtHours(report.totals.deviation_hours)} h
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog open={!!orderDialog} onClose={() => setOrderDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar orden de fabricacion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{orderDialog?.title}</Typography>
          {orders.length === 0 ? (
            <Alert severity="warning">
              Esta tarea no tiene proyecto asignado, asi que no hay ordenes que ofrecer.
              Asigna primero la tarea a un proyecto desde la orden correspondiente.
            </Alert>
          ) : (
            <TextField select fullWidth label="Orden" value={chosenOrder}
              onChange={(e) => setChosenOrder(e.target.value)}>
              {orders.map((o) => (
                <MenuItem key={o.id} value={o.id}>{o.code} — {o.name}</MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialog(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!chosenOrder} onClick={assignOrder}>
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      <TaskEditorDialog
        open={!!editor}
        taskId={editor?.taskId || null}
        onClose={() => setEditor(null)}
        onSaved={load}
      />
    </Box>
  );
}
